// Purpose: authenticated dropbox API wrappers (creator side). Follows the
// same CSRF + cookie pattern as api/shares.ts.

import { api } from './client.ts';

export type DropboxStatus = 'PENDING_APPROVAL' | 'ACTIVE' | 'EXPIRED' | 'REVOKED';
export type DropboxType = 'EPHEMERAL' | 'PERSISTENT';

export interface DropboxSummary {
  id: string;
  label: string;
  type: DropboxType;
  status: DropboxStatus;
  createdAt: string;
  approvedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  maxFiles: number;
  maxSizeBytes: string;
  fileCount: number;
  rotatedFromId: string | null;
}

export interface DropboxFileEntry {
  id: string;
  originalFilename: string;
  sizeBytes: string;
  mimeType: string;
  status: string;
  uploadedAt: string;
  availableAt: string | null;
  retrievedAt: string | null;
  dangerous: boolean;
}

export interface RotationChainEntry {
  id: string;
  status: DropboxStatus;
  createdAt: string;
  revokedAt: string | null;
}

export interface DropboxDetail {
  id: string;
  label: string;
  name: string;
  type: DropboxType;
  status: DropboxStatus;
  createdAt: string;
  approvedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  deleteOnRetrieve: boolean;
  maxFiles: number;
  maxSizeBytes: string;
  files: DropboxFileEntry[];
  rotationChain: RotationChainEntry[];
}

export interface CreateEphemeralResponse {
  dropboxId: string;
  linkUrl: string;
  expiresAt: string | null;
}

export interface PersistentRequestResponse {
  dropboxId: string;
  approvalId: string;
  status: 'PENDING_APPROVAL';
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith(name + '='));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function csrfHeaders(): Record<string, string> {
  const token = readCookie('ast_csrf');
  return token ? { 'X-CSRF-Token': token } : {};
}

export async function listDropboxes(): Promise<DropboxSummary[]> {
  const res = await api.get<{ dropboxes: DropboxSummary[] }>('/dropboxes');
  return res.data.dropboxes;
}

export async function getDropbox(id: string): Promise<DropboxDetail> {
  const res = await api.get<DropboxDetail>(`/dropboxes/${id}`);
  return res.data;
}

export type RoutingTarget = 'EPHEMERAL' | 'SHAREPOINT' | 'ONEDRIVE';

export interface RoutingConfigInput {
  target: RoutingTarget;
  siteId?: string;
  driveId?: string;
  folderPath?: string;
  notifyOnSuccess?: boolean;
  notifyOnFailure?: boolean;
}

export interface RoutingStatusSummary {
  dropboxId: string;
  routingTarget: RoutingTarget;
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
  routed: number;
  pending: number;
  failed: number;
  deadLettered: number;
}

export async function createEphemeralDropbox(input: {
  label: string;
  name?: string;
  expiresInHours: 4 | 24 | 168;
  deleteOnRetrieve?: boolean;
  routing?: RoutingConfigInput;
}): Promise<CreateEphemeralResponse> {
  const res = await api.post<CreateEphemeralResponse>('/dropboxes/ephemeral', input, {
    headers: csrfHeaders(),
  });
  return res.data;
}

export async function getRoutingStatus(id: string): Promise<RoutingStatusSummary> {
  const res = await api.get<RoutingStatusSummary>(`/dropboxes/${id}/routing-status`);
  return res.data;
}

export async function requestPersistentDropbox(input: {
  label: string;
  name?: string;
  justification: string;
  requestedExpiryDays: 30 | 90 | 365;
  deleteOnRetrieve?: boolean;
  routing?: RoutingConfigInput;
}): Promise<PersistentRequestResponse> {
  const res = await api.post<PersistentRequestResponse>('/dropboxes/persistent/request', input, {
    headers: csrfHeaders(),
  });
  return res.data;
}

export async function rotateDropbox(id: string): Promise<CreateEphemeralResponse> {
  const res = await api.post<CreateEphemeralResponse>(`/dropboxes/${id}/rotate`, null, {
    headers: csrfHeaders(),
  });
  return res.data;
}

export async function revokeDropbox(id: string): Promise<void> {
  await api.post(`/dropboxes/${id}/revoke`, null, { headers: csrfHeaders() });
}

// Commit 8b.1: owner link recovery. See apps/web/src/api/shares.ts
// `recoverShareLink` for the MFA 428 flow; same contract here.
export async function recoverDropboxLink(id: string): Promise<{ publicUrl: string }> {
  const res = await api.post<{ publicUrl: string }>(`/dropboxes/${id}/recover-link`, null, {
    headers: csrfHeaders(),
  });
  return res.data;
}
